import { Chip, Stack, Tooltip } from '@mui/material';
import { statusPalette, type StatusKey } from '@/theme/theme';

/**
 * The workflow states, in the exact Persian wording from `10-Glossary.md`.
 *
 * That document is a wording specification rather than a translation note: these strings
 * are the vocabulary the whole product speaks, and inventing a synonym here is how two
 * screens end up calling the same state different things.
 */
const STATE_LABELS: Record<string, { label: string; color: StatusKey }> = {
  Backlog: { label: 'بک‌لاگ', color: 'backlog' },
  Planned: { label: 'برنامه‌ریزی‌شده', color: 'planned' },
  InProgress: { label: 'در حال انجام', color: 'inProgress' },
  InReview: { label: 'در انتظار بازبینی', color: 'inReview' },
  Done: { label: 'انجام‌شده', color: 'done' },
};

/**
 * A workflow state.
 *
 * @remarks
 * The state is always spelled out, never conveyed by colour alone. Colour carries the
 * meaning faster for most people and not at all for some, which is why
 * `frontend-standards.md` section 12 requires the pairing.
 */
export function StatusChip({ state, size = 'small' }: { state: string; size?: 'small' | 'medium' }) {
  const entry = STATE_LABELS[state] ?? { label: state, color: 'backlog' as StatusKey };
  const color = statusPalette[entry.color];

  return (
    <Chip
      size={size}
      label={entry.label}
      sx={{
        color,
        backgroundColor: `${color}1F`,
        border: `1px solid ${color}55`,
        fontWeight: 600,
      }}
    />
  );
}

/**
 * The blocked flag.
 *
 * @remarks
 * Rendered beside the state rather than instead of it. Blocked is a parallel flag, not a
 * stage: a task can be blocked *and* in progress, and collapsing the two would lose which
 * one it is (`04-Domain-Model.md` section 3).
 */
export function BlockedChip() {
  return (
    <Tooltip title="این وظیفه مسدود است و تا رفع انسداد پیش نمی‌رود">
      <Chip
        size="small"
        label="مسدود"
        sx={{
          color: statusPalette.blocked,
          backgroundColor: `${statusPalette.blocked}1F`,
          border: `1px solid ${statusPalette.blocked}66`,
          fontWeight: 700,
        }}
      />
    </Tooltip>
  );
}

/** State and blocked together, in the order they should be read. */
export function TaskStatus({ state, isBlocked }: { state: string; isBlocked: boolean }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      {isBlocked ? <BlockedChip /> : null}
      <StatusChip state={state} />
    </Stack>
  );
}
